import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  TemplateRef,
  computed,
  contentChildren,
  inject,
  input,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { v4 as uuid } from 'uuid';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';

import { IconSize } from '../../icons/icons.interfaces';
import { RadioButtonTheme } from '../enums';
import { RadioItemGroupOption } from '../interfaces';

@Directive({
  selector: 'ng-template[fcomRadioItemGroupContent]',
  standalone: false,
})
export class RadioItemGroupContentDirective {
  readonly templateRef = inject(TemplateRef<unknown>);
  readonly value = input.required<string>({ alias: 'fcomRadioItemGroupContent' });
}

@Component({
  host: { '[class.fill]': 'isResponsive()' },
  selector: 'fcom-radio-item-group',
  templateUrl: './radio-item-group.component.html',
  styleUrls: ['./radio-item-group.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioItemGroupComponent {
  private readonly childContents = contentChildren(RadioItemGroupContentDirective);
  private readonly themeService = inject(FinnairThemeService);

  protected readonly hasTitleLabel = computed<boolean>(() => !!(this.translationPrefix() && this.titleText()));
  protected readonly isNewBrand = this.themeService.siteTheme() !== FinnairTheme.LEGACY;
  protected readonly twoItemMode = computed<boolean>(() => this.options().length === 2);

  readonly control = computed<AbstractControl>(() => this.parentForm().get(this.controlName()));
  readonly id = uuid();

  readonly RadioButtonTheme = RadioButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  /**
   * Fallback accessible name used when no title label is rendered (translationPrefix/titleText are false)
   */
  ariaLabel = input<string | undefined>();

  iconSize = input<IconSize>(IconSize.MEDIUM);

  theme = input(RadioButtonTheme.ROUND);

  parentForm = input.required<UntypedFormGroup>();

  controlName = input.required<string>();

  options = input.required<RadioItemGroupOption[]>();

  name = input<string | undefined>();

  isChild = input(false);

  isRequired = input(false);

  /**
   * Makes the radio item group responsive, adjusting its layout based on the screen size.
   */
  isResponsive = input(false);

  /**
   * This input is needed if you want to use title, helper or error text
   */
  translationPrefix = input<string | undefined>();

  /**
   * translationPrefix is needed if you want to use this text
   */
  helperText = input(false);

  /**
   * translationPrefix is needed if you want to use this text
   */
  titleText = input(true);

  /**
   * translationPrefix is needed if you want to use this text
   */
  errorText = input(true);

  /**
   * the field input hidden in analytics such as Hotjar
   */
  isSensitive = input(false);

  /**
   * The round theme is by default vertical, but it can be forced to horizontal
   */
  isHorizontal = input(false);

  /**
   * Adds rounded borders and extra padding to the radio item group.
   */
  showBorders = input(false);

  /**
   * Show a divider between different radio options (except the last one).
   */
  showDivider = input(false);

  /**
   * Set to `false` if you want to hide the required error message.
   * This is useful if you want to show a custom error message instead.
   */
  showRequiredError = input(true);

  /**
   * Optional prefix for test IDs on individual radio options
   */
  testIdPrefix = input<string | undefined>();

  protected getChildContent(value: string): TemplateRef<unknown> | undefined {
    return this.childContents().find((content) => content.value() === value)?.templateRef;
  }
}
